import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { KeyRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cambio password per chiunque sia entrato (condomino compreso): prima l'unica
// schermata stava nel wizard dell'amministratore e chiamava la rotta nel modo sbagliato.
export const ChangePassword = () => {
  const { token, user, refreshUser, refreshCondominiums, accessoComeCondomino } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error('La nuova password deve avere almeno 8 caratteri');
      return;
    }
    if (next !== confirm) {
      toast.error('Le due password non coincidono');
      return;
    }
    if (next === current) {
      toast.error('La nuova password deve essere diversa da quella attuale');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: current, new_password: next })
      });
      if (res.ok) {
        toast.success('Password cambiata. Dalla prossima volta entra con la nuova.');
        setCurrent(''); setNext(''); setConfirm('');
        const eraProvvisoria = Boolean(user?.force_password_change);
        if (refreshUser) await refreshUser();
        if (eraProvvisoria) {
          // prima del cambio le altre rotte erano chiuse: ora si caricano i condomini e si entra nel portale
          if (refreshCondominiums) await refreshCondominiums();
          navigate('/dashboard', { replace: true });
        }
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Cambio password non riuscito');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {user?.force_password_change && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          Stai usando una password provvisoria. Scegli una password tua per continuare: da quel momento entri con quella.
        </div>
      )}
      <PageHeader
        title="Cambia password"
        description={user?.email ? `Account ${user.email}` : 'La password con cui entri nel portale'}
      />
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-4 w-4" /> Nuova password
          </CardTitle>
          <CardDescription>Almeno 8 caratteri. Se hai dimenticato quella attuale, esci e nella pagina di accesso clicca «Se hai dimenticato la password, clicca qui»: l’amministratore ne imposterà una nuova e te la comunicherà.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pw-current">Password attuale</Label>
              <Input id="pw-current" type="password" autoComplete="current-password" value={current}
                     onChange={(e) => setCurrent(e.target.value)} required data-testid="pw-current" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw-next">Nuova password</Label>
              <Input id="pw-next" type="password" autoComplete="new-password" value={next}
                     onChange={(e) => setNext(e.target.value)} required minLength={8} data-testid="pw-next" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw-confirm">Ripeti la nuova password</Label>
              <Input id="pw-confirm" type="password" autoComplete="new-password" value={confirm}
                     onChange={(e) => setConfirm(e.target.value)} required minLength={8} data-testid="pw-confirm" />
            </div>
            <Button type="submit" disabled={loading} data-testid="pw-submit">
              {loading ? 'Salvataggio…' : 'Salva la nuova password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChangePassword;
